import clsx from "clsx";
import { motion } from "framer-motion";
import { useLocale } from "@/i18n";
import { staggerContainer, staggerItem, usePrefersReducedMotion } from "@/lib/motion";
import { formatCurrency } from "@/lib/money";
import { CheckIcon } from "@/components/ui/Icons";
import type { PublicCategory, UpsellingDisplaySettings } from "@/types";

type CategorySelectionCardsProps = {
  categories: PublicCategory[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  upselling: UpsellingDisplaySettings;
  showAvailabilityCount?: boolean;
};

export function CategorySelectionCards({
  categories,
  selectedId,
  onSelect,
  upselling,
  showAvailabilityCount = true,
}: CategorySelectionCardsProps) {
  const { messages: m, locale } = useLocale();
  const reducedMotion = usePrefersReducedMotion();

  const Container = reducedMotion ? "div" : motion.div;
  const Item = reducedMotion ? "button" : motion.button;

  const anyImages =
    upselling.show_category_images &&
    categories.some((cat) => (cat.images?.length ?? 0) > 0);

  return (
    <Container
      className="bk-category-grid"
      role="radiogroup"
      aria-label={m.flow.selectPitch}
      {...(reducedMotion
        ? {}
        : {
            variants: staggerContainer,
            initial: "hidden",
            animate: "visible",
          })}
    >
      {categories.map((cat, index) => {
        const id = (cat.pitch_category_id ?? "").trim();
        const selected =
          selectedId !== null &&
          selectedId.trim().toLowerCase() === id.toLowerCase() &&
          id.length > 0;
        const images = cat.images ?? [];
        const imageUrl =
          upselling.show_category_images && images.length > 0 ? images[0]?.image_url : null;
        const showTexts = upselling.show_marketing_texts;
        const badgeText = upselling.show_badges ? cat.badge_text?.trim() : null;
        const recommended = cat.highlight_recommended && upselling.auto_highlight_recommended;
        const showRecommendedRibbon =
          recommended && badgeText?.toLowerCase() !== m.category.recommended.toLowerCase();
        const features = showTexts ? (cat.features ?? []).slice(0, 4) : [];
        const nights = cat.nights ?? 0;

        return (
          <Item
            key={id || `category-${index}-${cat.name}`}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={selected ? `${cat.name}, ${m.category.selected}` : cat.name}
            onClick={() => onSelect(id)}
            className={clsx(
              "bk-category-card",
              selected && "bk-category-card--selected",
              !anyImages && "bk-category-card--no-media",
            )}
            {...(reducedMotion ? {} : { variants: staggerItem })}
          >
            <span className="bk-category-card__check">
              <CheckIcon />
            </span>
            {anyImages ? (
              <div className="bk-category-card__media">
                {imageUrl ? (
                  <img src={imageUrl} alt={images[0]?.alt_text || cat.name} loading="lazy" />
                ) : null}
              </div>
            ) : null}
            {(showRecommendedRibbon || badgeText) && anyImages ? (
              <div className="bk-category-card__ribbons">
                {showRecommendedRibbon ? (
                  <span className="bk-category-card__ribbon">{m.category.recommended}</span>
                ) : null}
                {badgeText ? (
                  <span className="bk-category-card__ribbon bk-category-card__ribbon--badge">
                    {badgeText}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="bk-category-card__body">
              {(showRecommendedRibbon || badgeText) && !anyImages ? (
                <div className="bk-category-card__ribbons">
                  {showRecommendedRibbon ? (
                    <span className="bk-category-card__ribbon">{m.category.recommended}</span>
                  ) : null}
                  {badgeText ? (
                    <span className="bk-category-card__ribbon bk-category-card__ribbon--badge">
                      {badgeText}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="bk-category-card__head">
                <h3 className="bk-category-card__title">{cat.name}</h3>
                {cat.nightly_rate ? (
                  <div className="bk-category-card__price">
                    <span className="bk-category-card__price-from">{m.category.from}</span>
                    <span className="bk-category-card__price-amount">
                      {formatCurrency(cat.nightly_rate, "EUR", locale)}
                    </span>
                    <span className="bk-category-card__price-unit">
                      {m.category.perPeriod[
                        cat.rental_period && cat.rental_period in m.category.perPeriod
                          ? (cat.rental_period as keyof typeof m.category.perPeriod)
                          : "night"
                      ]}
                    </span>
                  </div>
                ) : null}
              </div>
              {showTexts && cat.teaser_text ? (
                <p className="bk-category-card__teaser">{cat.teaser_text}</p>
              ) : null}
              {features.length > 0 ? (
                <ul className="bk-category-card__features">
                  {features.map((feature) => (
                    <li key={feature}>
                      <CheckIcon />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {upselling.show_price_comparison && cat.price_comparison_text ? (
                <p className="bk-category-card__comparison">{cat.price_comparison_text}</p>
              ) : null}
              {showTexts && cat.hint_text ? (
                <p className="bk-category-card__hint">{cat.hint_text}</p>
              ) : null}
              <div className="bk-category-card__footer">
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  {showAvailabilityCount ? (
                    <span className="bk-category-card__availability">
                      {m.category.available(cat.available_count)}
                    </span>
                  ) : null}
                  {cat.price_pending ? (
                    <span className="bk-category-card__total">{m.category.priceFollows}</span>
                  ) : cat.total_amount && nights > 0 ? (
                    <span className="bk-category-card__total">
                      {m.category.total}:{" "}
                      <strong>{formatCurrency(cat.total_amount, "EUR", locale)}</strong> · {nights}{" "}
                      {nights === 1 ? m.common.night : m.common.nights}
                    </span>
                  ) : cat.nightly_rate ? (
                    <span className="bk-category-card__total">{m.category.priceHint}</span>
                  ) : null}
                </div>
                <span className="bk-category-card__cta">
                  {selected ? m.category.selected : m.category.select}
                </span>
              </div>
            </div>
          </Item>
        );
      })}
    </Container>
  );
}
